import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { categories } from '../data/mock';
import { useLightboxControls } from '../hooks/use-lightbox-controls';
import { ShareMenu } from './ShareMenu';

const iconBtn = 'flex items-center justify-center w-11 h-11 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors';
const sans = { fontFamily: 'Montserrat, sans-serif' };

export const Lightbox = ({ photos, index, onClose, onIndexChange }) => {
  const navigate = useNavigate();
  const isOpen = index !== null && index >= 0 && index < photos.length;
  const count = photos.length;

  const onPrev = useCallback(() => onIndexChange((index - 1 + count) % count), [index, count, onIndexChange]);
  const onNext = useCallback(() => onIndexChange((index + 1) % count), [index, count, onIndexChange]);
  const { onTouchStart, onTouchEnd } = useLightboxControls({ isOpen, onClose, onPrev, onNext });

  if (!isOpen) return null;

  const photo = photos[index];
  const categoryName = categories.find(c => c.id === photo.category)?.name || photo.category;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/95 backdrop-blur-sm select-none"
      role="dialog"
      aria-modal="true"
      aria-label={photo.title}
      data-testid="lightbox"
    >
      <div className="relative z-10 flex items-center justify-between px-4 py-3 text-xs tracking-widest uppercase text-white/60" style={sans}>
        <span data-testid="lightbox-counter">{index + 1} / {count}</span>
        <div className="flex items-center gap-1">
          <ShareMenu key={photo.id} photo={photo} categoryName={categoryName} />
          <button onClick={onClose} className={iconBtn} aria-label="Close" data-testid="lightbox-close-btn">
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div
        className="relative flex-1 flex items-center justify-center min-h-0 px-2"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onClick={onClose}
      >
        <img
          key={photo.id}
          src={photo.image}
          alt={photo.title}
          className="max-h-full max-w-full object-contain fade-in-up"
          style={{ animationDuration: '0.35s' }}
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-image"
        />
        {count > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className={`${iconBtn} absolute left-2 top-1/2 -translate-y-1/2 hidden sm:flex`} aria-label="Previous photo" data-testid="lightbox-prev-btn">
              <ChevronLeft className="w-7 h-7" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onNext(); }} className={`${iconBtn} absolute right-2 top-1/2 -translate-y-1/2 hidden sm:flex`} aria-label="Next photo" data-testid="lightbox-next-btn">
              <ChevronRight className="w-7 h-7" />
            </button>
          </>
        )}
      </div>

      <div className="flex items-center justify-center px-6 py-4 border-t border-white/10">
        <button
          onClick={() => navigate('/contact', { state: { category: photo.category } })}
          className="hero-cta"
          style={{ marginTop: 0, padding: '0.6rem 1.5rem', fontSize: '11px', lineHeight: '1.2' }}
          data-testid="lightbox-book-btn"
        >
          Book a Session
        </button>
      </div>
    </div>
  );
};
